import { useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, Eye, Info, MousePointerClick, ThumbsUp, Users } from 'lucide-react';

import { format } from 'date-fns';

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { getTitle } from '@/lib/channels';
import { useCancelSubscription } from '@/hooks/useCancelSubscription';
import { useChannelActions } from '@/hooks/useChannelActions';
import { useChannelDefinitions } from '@/hooks/useChannelDefinitions';
import { useChannels } from '@/hooks/useChannels';
import { useGbpTotals, useLocalAds } from '@/hooks/useInsights';
import { useInventoryPageSummary } from '@/hooks/useInventory';
import { useShop } from '@/hooks/useShop';

/**
 * Close-account flow on the Billing page (NEA-6014), against Max's updated
 * design (fresh-dashboard@344d722). The checklist covers every live channel:
 * the design's curated rows where they apply, plus a row per enabled channel
 * the curated set doesn't cover. Two routes through it:
 *
 *   - **Full cancellation** — every visible row ticked (or none to begin
 *     with) → loss-aversion warning →
 *     `POST /api/billing/subscription/{shopId}/cancel`. The server branches on
 *     the org's billable-shop count: a single location (or the chain's last
 *     active store) sets `cancel_at_period_end: true` on the shared sub, while
 *     a chain closes ONLY this location (`mode: 'location'` in the response —
 *     the store leaves the plan at period end, siblings unaffected). The copy
 *     here mirrors that split via `chainClose` (frontend prediction) and the
 *     response's `mode` (server truth, drives the done dialog). Either way the
 *     customer keeps full access until period end and Email 23 fires.
 *   - **Partial cancellation** — some rows ticked → a confirm modal, and on
 *     Confirm the rows' live channels are disabled (no email, account stays
 *     active). LIA gets a belt-and-braces treatment: `disable('local-ads')`
 *     flips the channel flag AND `updateBudget(0)` zeros the spend in the
 *     plans-{stage} table, since LIA has two independent backings.
 */

const CURATED_GROUPS = [
	{ id: 'storefront', label: 'Local Storefront page', channels: ['storefront'] },
	{ id: 'g-listings', label: 'Google Local Listings', channels: ['google'] },
	{ id: 'g-ads', label: 'Google Local Inventory Ads', channels: ['local-ads'] },
	{ id: 'orders', label: 'Local Checkout + Order history', channels: ['local-checkout', 'product-reservations'] }
];

const CURATED_CHANNEL_TYPES = new Set(CURATED_GROUPS.flatMap((group) => group.channels));

const NUMBER_FORMAT = new Intl.NumberFormat('en-GB');

export function CancelSubscriptionFlow({ shopId }) {
	const [step, setStep] = useState('closed');
	const [selected, setSelected] = useState(() => new Set());
	const [pending, setPending] = useState(false);
	const [error, setError] = useState(null);
	const [cancelResult, setCancelResult] = useState(null);

	const { shop, shops = [] } = useShop();
	const { cancel } = useCancelSubscription();
	const { disable: disableChannel } = useChannelActions(shopId);
	const { updateBudget } = useLocalAds(shopId);
	const { channels } = useChannels(shopId);
	const { definitions } = useChannelDefinitions();
	// A falsy shopId nulls these hooks' SWR keys, so the Aurora/Typesense stats
	// reads only fire once the flow is opened — their data is only rendered in
	// the warn dialog, which most Billing-page visitors never reach.
	const statsShopId = step !== 'closed' ? shopId : null;
	const { views, interactions, hasData: hasGbpData } = useGbpTotals(statsShopId, 30);
	const { summary } = useInventoryPageSummary(statsShopId);

	// Frontend prediction of the server's billable-shop branch (same rule as
	// `isBillableShop` + the pending-close exclusion): other active stores on
	// the org → this flow closes ONE location, not the whole account. The
	// server decides authoritatively; the response's `mode` drives the done
	// dialog.
	const chainClose = shops.some(
		(s) => s.id !== shopId && s.enabled !== false && !s.closeAt && s.testMode !== true && s.internal !== true
	);
	const storeName = shop?.name ?? 'this store';

	const enabledTypes = new Set(channels.filter((channel) => channel.status === 'enabled').map((channel) => channel.type));
	const items = [
		...CURATED_GROUPS.filter((group) => group.channels.some((type) => enabledTypes.has(type))),
		...[...enabledTypes]
			.filter((type) => !CURATED_CHANNEL_TYPES.has(type))
			.map((type) => ({ id: type, label: getTitle(type, definitions?.[type]) ?? type, channels: [type] }))
	];

	// The shop's REAL numbers (last 30 days of GBP insights + live catalogue) —
	// a row with no data is dropped rather than shown as a made-up figure.
	const totalItems = summary?.totalItems ?? 0;
	const stats = [
		hasGbpData &&
			views > 0 && {
				icon: <Eye className="h-4 w-4" />,
				value: NUMBER_FORMAT.format(views),
				label: 'local shopper journeys in the last 30 days'
			},
		hasGbpData &&
			interactions > 0 && {
				icon: <MousePointerClick className="h-4 w-4" />,
				value: NUMBER_FORMAT.format(interactions),
				label: 'local customer engagements in the last 30 days'
			},
		totalItems > 0 &&
			enabledTypes.size > 0 && {
				icon: <Users className="h-4 w-4" />,
				value: NUMBER_FORMAT.format(totalItems),
				label: `in-store products shown in ${enabledTypes.size} ${enabledTypes.size === 1 ? 'place' : 'places'}`
			}
	].filter(Boolean);

	const close = () => {
		if (pending) return;
		setStep('closed');
		setSelected(new Set());
		setError(null);
	};

	const toggle = (id) => {
		if (pending) return;
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const count = selected.size;
	const nothingListed = items.length === 0;
	const allChecked = !nothingListed && count === items.length;
	const partial = count > 0 && !allChecked;
	const fullCancel = allChecked || nothingListed;

	const handleConfirmCancel = async () => {
		setPending(true);
		setError(null);
		try {
			const result = await cancel();
			setCancelResult(result);
			setStep('done');
		} catch (err) {
			setError(err?.response?.data?.error ?? err?.message ?? 'Something went wrong. Please try again.');
		} finally {
			setPending(false);
		}
	};

	const handlePartialDisable = async () => {
		setPending(true);
		setError(null);
		try {
			for (const group of items.filter((i) => selected.has(i.id))) {
				for (const type of group.channels.filter((t) => enabledTypes.has(t))) {
					await disableChannel(type);
					if (type === 'local-ads') {
						await updateBudget(0);
					}
				}
			}
			setPending(false);
			close();
		} catch (err) {
			setPending(false);
			setError(err?.response?.data?.error ?? err?.message ?? 'Something went wrong. Please try again.');
		}
	};

	return (
		<>
			<div className="flex items-center justify-between gap-4 border-t border-border/60 pb-2 pt-5">
				<div className="min-w-0">
					<div className="text-sm font-semibold text-muted-foreground">
						{chainClose ? 'Close this store' : 'Close your Unify account'}
					</div>
					<div className="mt-1 text-xs text-muted-foreground/80">
						{chainClose
							? `This removes ${storeName} from your plan and switches off its storefront and channels. Your other stores are unaffected.`
							: "This permanently deletes your storefront, channels, orders and customer data. It can't be undone."}
					</div>
				</div>
				<button
					type="button"
					onClick={() => setStep('confirm')}
					className="whitespace-nowrap text-xs font-medium text-muted-foreground underline underline-offset-4 transition hover:text-destructive"
				>
					{chainClose ? 'Close store' : 'Close account'}
				</button>
			</div>

			{/* Step — selective checklist */}
			<Dialog open={step === 'confirm'} onOpenChange={(o) => !o && close()}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle className="text-xl font-extrabold tracking-tight">What would you like to cancel?</DialogTitle>
						<DialogDescription>
							Pick what you want to stop. You can turn off a channel and keep the rest of Unify running.
						</DialogDescription>
					</DialogHeader>

					{nothingListed ? (
						<p className="rounded-xl border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
							{chainClose
								? 'This store has no live channels — cancelling will close this store.'
								: 'You have no live channels — cancelling will close your Unify account.'}
						</p>
					) : null}

					<ul className="max-h-[44vh] space-y-2 overflow-y-auto pr-1">
						{items.map((item) => {
							const checked = selected.has(item.id);
							return (
								<li key={item.id}>
									<label
										className={cn(
											'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition',
											checked ? 'border-foreground/30 bg-muted/50' : 'border-border hover:bg-muted/30'
										)}
									>
										<span className="relative mt-0.5 inline-flex h-4 w-4 shrink-0">
											<input
												type="checkbox"
												checked={checked}
												onChange={() => toggle(item.id)}
												className="h-4 w-4 cursor-pointer appearance-none rounded-full border border-primary checked:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
											/>
											{checked ? (
												<Check className="pointer-events-none absolute inset-0 m-auto h-3 w-3 text-primary-foreground" />
											) : null}
										</span>
										<span className="text-sm font-medium text-foreground">{item.label}</span>
									</label>
								</li>
							);
						})}
					</ul>

					{fullCancel ? (
						<div className="flex items-start gap-2.5 rounded-xl border border-destructive/30 bg-destructive/10 p-3">
							<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
							<p className="text-xs font-medium text-destructive">
								{chainClose
									? `${storeName} will be closed and removed from your plan. Your other stores stay live.`
									: 'Your Unify account will be deleted.'}
							</p>
						</div>
					) : null}
					{partial ? (
						<div className="flex items-start gap-2.5 rounded-xl border border-primary/20 bg-primary/5 p-3">
							<Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
							<p className="text-xs font-medium text-foreground">
								These channels will be switched off. Your Unify account stays active.
							</p>
						</div>
					) : null}

					<DialogFooter className="!flex-col-reverse gap-2 pt-2 sm:!flex-row sm:justify-end">
						<button
							type="button"
							onClick={close}
							disabled={pending}
							className="rounded-xl px-4 py-2.5 text-sm font-semibold text-muted-foreground transition hover:text-foreground disabled:opacity-50"
						>
							Keep everything
						</button>
						<button
							type="button"
							onClick={fullCancel ? () => setStep('warn') : () => setStep('partial-confirm')}
							disabled={(count === 0 && !nothingListed) || pending}
							className={cn(
								'whitespace-nowrap rounded-xl px-5 py-2.5 text-sm font-semibold transition',
								count === 0 && !nothingListed && 'cursor-not-allowed bg-muted text-muted-foreground',
								fullCancel && 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
								partial && 'bg-foreground text-background hover:bg-foreground/90'
							)}
						>
							{fullCancel
								? chainClose
									? 'Continue to close this store'
									: 'Continue to cancel account'
								: partial
									? 'Keep Unify, but stop these channels'
									: 'Confirm cancellation'}
						</button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Step — loss-aversion warning (full path only) */}
			<Dialog open={step === 'warn'} onOpenChange={(o) => !o && close()}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle className="text-xl font-extrabold tracking-tight">
							Don't lose these customers looking for you
						</DialogTitle>
						<DialogDescription>
							{chainClose
								? `By closing ${storeName}, it will stop appearing in:`
								: 'By cancelling, your store will stop appearing in:'}
						</DialogDescription>
					</DialogHeader>

					{stats.length > 0 ? (
						<div className="space-y-2">
							{stats.map((stat) => (
								<StatRow key={stat.label} icon={stat.icon} value={stat.value} label={stat.label} />
							))}
						</div>
					) : null}

					<p className="text-xs leading-relaxed text-muted-foreground">
						{chainClose
							? "You'll also remove this store's visibility on ChatGPT, Google Gemini, and every other AI search tool — and lose the product data, customer insights, and results it's been getting."
							: "By cancelling Unify, you'll also remove visibility on ChatGPT, Google Gemini, and every other AI search tool — and lose the product data, customer insights, and results you've been getting."}
					</p>

					{error ? <InlineError message={error} /> : null}

					<DialogFooter className="!flex-col-reverse gap-2 pt-2 sm:!flex-row sm:justify-end">
						<button
							type="button"
							onClick={handleConfirmCancel}
							disabled={pending}
							className="rounded-xl px-4 py-2.5 text-sm font-semibold text-muted-foreground transition hover:text-foreground disabled:opacity-50"
						>
							{pending ? 'Cancelling…' : 'Confirm cancellation'}
						</button>
						<button
							type="button"
							onClick={close}
							disabled={pending}
							className="rounded-xl bg-gradient-unify px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
						>
							<ThumbsUp className="mr-1.5 inline h-4 w-4" /> Stay with Unify
						</button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Step — full-cancel confirmation */}
			<Dialog open={step === 'done'} onOpenChange={(o) => !o && close()}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
							<CheckCircle2 className="h-6 w-6" />
						</div>
						<DialogTitle className="text-xl font-extrabold tracking-tight">Your cancellation is confirmed</DialogTitle>
						<DialogDescription>
							{cancelResult?.mode === 'location'
								? `We've received your request to close ${storeName}. It stays live until ${
										cancelResult?.accessEndsAt
											? format(new Date(cancelResult.accessEndsAt), 'd MMMM yyyy')
											: 'the end of your current billing period'
									} — after that its storefront and channels will switch off. Your other stores and your Unify subscription are unaffected.`
								: "We've received your request to close your Unify account. You'll keep full access to your dashboard until the end of your current billing period — after that, your storefront, channels and data will be removed."}
						</DialogDescription>
					</DialogHeader>

					<DialogFooter className="!flex-col-reverse gap-2 pt-2 sm:!flex-row sm:justify-end">
						<button
							type="button"
							onClick={close}
							className="rounded-xl bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition hover:bg-foreground/90"
						>
							Back to dashboard
						</button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Step — partial-cancel confirm (channels disable on Confirm) */}
			<Dialog open={step === 'partial-confirm'} onOpenChange={(o) => !o && close()}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
							<CheckCircle2 className="h-6 w-6" />
						</div>
						<DialogTitle className="text-xl font-extrabold tracking-tight">These channels will be switched off</DialogTitle>
						<DialogDescription>Your Unify account stays active. The following will stop:</DialogDescription>
					</DialogHeader>

					<ul className="max-h-[44vh] space-y-2 overflow-y-auto pr-1">
						{items
							.filter((i) => selected.has(i.id))
							.map((item) => (
								<li key={item.id} className="flex items-start gap-3 rounded-xl border border-border p-3">
									<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
									<span className="text-sm font-medium text-foreground">{item.label}</span>
								</li>
							))}
					</ul>

					{error ? <InlineError message={error} /> : null}

					<DialogFooter className="!flex-col-reverse gap-2 pt-2 sm:!flex-row sm:justify-end">
						<button
							type="button"
							onClick={close}
							disabled={pending}
							className="rounded-xl bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition hover:bg-foreground/90 disabled:opacity-50"
						>
							Back to dashboard
						</button>
						<button
							type="button"
							onClick={handlePartialDisable}
							disabled={pending}
							className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
						>
							{pending ? 'Switching off…' : 'Confirm'}
						</button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

function InlineError({ message }) {
	return (
		<div className="flex items-start gap-2.5 rounded-xl border border-destructive/30 bg-destructive/10 p-3">
			<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
			<p className="text-xs font-medium text-destructive">{message}</p>
		</div>
	);
}

function StatRow({ icon, value, label }) {
	return (
		<div className="flex items-center gap-3 rounded-xl border border-border p-3">
			<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
				{icon}
			</div>
			<div className="min-w-0">
				<div className="text-lg font-extrabold leading-tight text-foreground">{value}</div>
				<div className="text-xs text-muted-foreground">{label}</div>
			</div>
		</div>
	);
}
